import type { Bitmap } from "@squire/sprites";

export type Surface = HTMLCanvasElement | OffscreenCanvas;

const rgbaCache = new Map<string, number>();
function rgba(hex: string): number {
  let v = rgbaCache.get(hex);
  if (v === undefined) {
    const n = parseInt(hex.slice(1), 16);
    // little-endian ABGR for Uint32Array over ImageData
    v = (0xff << 24) | ((n & 0xff) << 16) | (n & 0xff00) | ((n >> 16) & 0xff);
    rgbaCache.set(hex, v >>> 0);
    v = v >>> 0;
  }
  return v;
}

function makeSurface(w: number, h: number): Surface {
  if (typeof OffscreenCanvas !== "undefined") return new OffscreenCanvas(w, h);
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  return c;
}

/** Paint a bitmap into a new surface (one-time cost, then blitted with drawImage). */
export function bitmapToSurface(bm: Bitmap): Surface {
  const surface = makeSurface(bm.w, bm.h);
  const ctx = surface.getContext("2d") as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;
  if (!ctx) return surface;
  const img = ctx.createImageData(bm.w, bm.h);
  const px = new Uint32Array(img.data.buffer);
  for (let i = 0; i < bm.data.length; i++) {
    const c = bm.data[i];
    if (c) px[i] = rgba(c);
  }
  ctx.putImageData(img, 0, 0);
  return surface;
}

/**
 * Bounded LRU cache of composed frames (SPEC 8: no growing structures).
 * Map keeps insertion order; re-inserting on hit makes it LRU.
 */
export class SurfaceCache {
  private map = new Map<string, Surface>();
  constructor(private readonly max = 192) {}

  get(key: string, make: () => Surface): Surface {
    const hit = this.map.get(key);
    if (hit) {
      this.map.delete(key);
      this.map.set(key, hit);
      return hit;
    }
    const s = make();
    this.map.set(key, s);
    while (this.map.size > this.max) {
      const oldest = this.map.keys().next().value as string;
      this.map.delete(oldest);
    }
    return s;
  }

  get size(): number {
    return this.map.size;
  }

  clear(): void {
    this.map.clear();
  }
}
